import { supabase } from '@/lib/supabase';

export interface AuditLog {
  id: string;
  action: string;
  details: string;
  profile_id: string | null;
  created_at: string;
  profiles?: {
    id: string;
    name: string;
    email: string;
    avatar_url: string | null;
  } | null;
}

export interface RecentActivity {
  id: string;
  type: 'associado' | 'reserva' | 'pagamento' | 'empresa' | 'evento' | 'dependente' | 'carteirinha';
  title: string;
  description: string;
  timestamp: string;
  status?: 'success' | 'pending' | 'warning' | 'error';
  value?: number;
  user?: string;
  userInitials?: string;
  userAvatarUrl?: string | null;
}

export class AuditService {
  /**
   * Busca logs de auditoria recentes
   */
  static async getRecentAuditLogs(limit: number = 10): Promise<AuditLog[]> {
    try {
      const { data, error } = await supabase
        .from('audit_logs')
        .select(
          `
          id,
          action,
          details,
          profile_id,
          created_at,
          profiles!audit_logs_profile_id_fkey(
            id,
            name,
            email,
            avatar_url
          )
        `
        )
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Erro ao buscar logs de auditoria:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Erro no AuditService.getRecentAuditLogs:', error);
      throw error;
    }
  }

  /**
   * Converte logs de auditoria em atividades recentes formatadas
   */
  static formatAuditLogsToActivities(logs: AuditLog[]): RecentActivity[] {
    return logs.map((log) => {
      const activity = this.parseActivityFromLog(log);

      // Usar nome real do usuário se disponível
      let userName = 'Usuário do Sistema';
      let userInitials = 'US';

      if (log.profiles?.name) {
        userName = log.profiles.name;
        userInitials = this.getUserInitials(log.profiles.name);
      } else if (log.profile_id) {
        // Fallback se não conseguir buscar o nome
        userName = `Usuário ${log.profile_id.slice(0, 8)}`;
        userInitials = 'U' + log.profile_id.slice(0, 1).toUpperCase();
      }

      return {
        id: log.id,
        type: activity.type,
        title: activity.title,
        description: activity.description,
        timestamp: log.created_at,
        status: activity.status,
        value: activity.value,
        user: userName,
        userInitials: userInitials,
        userAvatarUrl: log.profiles?.avatar_url || null,
      };
    });
  }

  /**
   * Parseia a atividade a partir do log de auditoria
   */
  private static parseActivityFromLog(log: AuditLog) {
    const action = log.action.toLowerCase();
    const details = JSON.parse(log.details || '{}');

    // Mapeamento de ações para tipos de atividade
    if (action.includes('associado') || action.includes('associados')) {
      return this.parseAssociadoActivity(action, details);
    }

    if (action.includes('reserva') || action.includes('reservas')) {
      return this.parseReservaActivity(action, details);
    }

    if (action.includes('pagamento') || action.includes('pagamentos')) {
      return this.parsePagamentoActivity(action, details);
    }

    if (action.includes('empresa') || action.includes('empresas')) {
      return this.parseEmpresaActivity(action, details);
    }

    if (action.includes('dependente') || action.includes('dependentes')) {
      return this.parseDependenteActivity(action, details);
    }

    if (action.includes('carteirinha') || action.includes('carteirinhas')) {
      return this.parseCarteirinhaActivity(action, details);
    }

    // Atividade genérica
    return {
      type: 'evento' as const,
      title: this.formatActionTitle(action),
      description: this.formatActionDescription(action, details),
      status: 'success' as const,
    };
  }

  /**
   * Parseia atividades de associados
   */
  private static parseAssociadoActivity(action: string, details: any) {
    const nome = details.nome || details.name || 'Associado';

    if (action.includes('create') || action.includes('criar') || action.includes('cadastrar')) {
      return {
        type: 'associado' as const,
        title: 'Novo Associado',
        description: `${nome} foi cadastrado no sistema`,
        status: 'success' as const,
      };
    }

    if (action.includes('update') || action.includes('atualizar') || action.includes('editar')) {
      return {
        type: 'associado' as const,
        title: 'Associado Atualizado',
        description: `Dados de ${nome} foram atualizados`,
        status: 'success' as const,
      };
    }

    if (action.includes('delete') || action.includes('deletar') || action.includes('remover')) {
      return {
        type: 'associado' as const,
        title: 'Associado Removido',
        description: `${nome} foi removido do sistema`,
        status: 'warning' as const,
      };
    }

    return {
      type: 'associado' as const,
      title: 'Ação em Associado',
      description: `Ação realizada em ${nome}`,
      status: 'success' as const,
    };
  }

  /**
   * Parseia atividades de reservas
   */
  private static parseReservaActivity(action: string, details: any) {
    const local = details.local || details.nome || 'Local';
    const hospedes = details.hospedes || details.guests || '';

    if (action.includes('create') || action.includes('criar') || action.includes('reservar')) {
      return {
        type: 'reserva' as const,
        title: 'Nova Reserva',
        description: `${local}${hospedes ? ` - ${hospedes} hóspedes` : ''}`,
        status: 'success' as const,
        value: details.valor || details.value,
      };
    }

    if (action.includes('confirm') || action.includes('confirmar')) {
      return {
        type: 'reserva' as const,
        title: 'Reserva Confirmada',
        description: `${local}${hospedes ? ` - ${hospedes} hóspedes` : ''}`,
        status: 'success' as const,
        value: details.valor || details.value,
      };
    }

    if (action.includes('cancel') || action.includes('cancelar')) {
      return {
        type: 'reserva' as const,
        title: 'Reserva Cancelada',
        description: `${local} foi cancelada`,
        status: 'error' as const,
      };
    }

    return {
      type: 'reserva' as const,
      title: 'Ação em Reserva',
      description: `Ação realizada em ${local}`,
      status: 'success' as const,
    };
  }

  /**
   * Parseia atividades de pagamentos
   */
  private static parsePagamentoActivity(action: string, details: any) {
    const associado = details.associado || details.nome || 'Associado';
    const tipo = details.tipo || details.type || 'Mensalidade';

    if (action.includes('create') || action.includes('criar') || action.includes('receber')) {
      return {
        type: 'pagamento' as const,
        title: 'Pagamento Recebido',
        description: `${tipo} associado - ${associado}`,
        status: 'success' as const,
        value: details.valor || details.value || details.amount,
      };
    }

    return {
      type: 'pagamento' as const,
      title: 'Ação em Pagamento',
      description: `Ação realizada em pagamento de ${associado}`,
      status: 'success' as const,
    };
  }

  /**
   * Parseia atividades de empresas
   */
  private static parseEmpresaActivity(action: string, details: any) {
    const nome = details.nome || details.name || 'Empresa';

    if (action.includes('create') || action.includes('criar') || action.includes('cadastrar')) {
      return {
        type: 'empresa' as const,
        title: 'Nova Empresa Parceira',
        description: `${nome} foi cadastrada`,
        status: 'success' as const,
      };
    }

    if (action.includes('update') || action.includes('atualizar') || action.includes('editar')) {
      return {
        type: 'empresa' as const,
        title: 'Empresa Atualizada',
        description: `Dados de ${nome} foram atualizados`,
        status: 'success' as const,
      };
    }

    return {
      type: 'empresa' as const,
      title: 'Ação em Empresa',
      description: `Ação realizada em ${nome}`,
      status: 'success' as const,
    };
  }

  /**
   * Parseia atividades de dependentes
   */
  private static parseDependenteActivity(action: string, details: any) {
    const nome = details.nome || details.name || 'Dependente';

    if (action.includes('create') || action.includes('criar') || action.includes('adicionar')) {
      return {
        type: 'dependente' as const,
        title: 'Novo Dependente',
        description: `${nome} foi adicionado como dependente`,
        status: 'success' as const,
      };
    }

    if (action.includes('delete') || action.includes('deletar') || action.includes('remover')) {
      return {
        type: 'dependente' as const,
        title: 'Dependente Removido',
        description: `${nome} foi removido como dependente`,
        status: 'warning' as const,
      };
    }

    return {
      type: 'dependente' as const,
      title: 'Ação em Dependente',
      description: `Ação realizada em ${nome}`,
      status: 'success' as const,
    };
  }

  /**
   * Parseia atividades de carteirinhas
   */
  private static parseCarteirinhaActivity(action: string, details: any) {
    const associado = details.associado || details.nome || 'Associado';

    if (action.includes('create') || action.includes('criar') || action.includes('gerar')) {
      return {
        type: 'carteirinha' as const,
        title: 'Carteirinha Gerada',
        description: `Carteirinha gerada para ${associado}`,
        status: 'success' as const,
      };
    }

    return {
      type: 'carteirinha' as const,
      title: 'Ação em Carteirinha',
      description: `Ação realizada na carteirinha de ${associado}`,
      status: 'success' as const,
    };
  }

  /**
   * Formata título da ação
   */
  private static formatActionTitle(action: string): string {
    const actionMap: { [key: string]: string } = {
      create: 'Nova Atividade',
      update: 'Atividade Atualizada',
      delete: 'Atividade Removida',
      criar: 'Nova Atividade',
      atualizar: 'Atividade Atualizada',
      deletar: 'Atividade Removida',
      cadastrar: 'Nova Atividade',
      editar: 'Atividade Atualizada',
      remover: 'Atividade Removida',
    };

    return actionMap[action] || 'Atividade do Sistema';
  }

  /**
   * Formata descrição da ação
   */
  private static formatActionDescription(action: string, details: any): string {
    const entity = details.entity || details.entidade || 'item';
    return `Ação "${action}" realizada em ${entity}`;
  }

  /**
   * Gera iniciais do usuário
   */
  private static getUserInitials(name: string): string {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }

  /**
   * Registra uma nova atividade no log de auditoria
   */
  static async logActivity(action: string, details: any, profileId?: string): Promise<void> {
    try {
      const { error } = await supabase.from('audit_logs').insert({
        action,
        details: JSON.stringify(details),
        profile_id: profileId,
      });

      if (error) {
        console.error('Erro ao registrar atividade:', error);
        throw error;
      }
    } catch (error) {
      console.error('Erro no AuditService.logActivity:', error);
      throw error;
    }
  }
}
